import { memo, useEffect, useRef } from "react";
import styled from "styled-components";
import { SystemManagedDataEntity } from "@lifeat/shared/data-models/system/SystemManagedDataEntity";
import { SpaceCategory, SpaceCategoryLabels } from "@lifeat/shared/data-models/virtual-space/SpaceCategory";
import { RecommendationFrequency } from "@lifeat/shared/data-models/virtual-space/VirtualSpace";
import { getSpaceThumbnail } from "@lifeat/shared/spaces/SpacesUtil";
import { LifeAtButtonV2 } from "@common/LifeAtButtonV2/LifeAtButtonV2";
import LifeAtCheckbox from "@common/LifeAtCheckbox/LifeAtCheckbox";
import { LifeAtSingleSelectDropdown } from "@common/LifeAtSingleSelectDropdown";
import { LifeAtTextInputV2 } from "@common/LifeAtTextInput/LifeAtTextInputV2";

type Space = SystemManagedDataEntity["spaces"][string];

const RecommendationFrequencyLabels: { [frequency in RecommendationFrequency]: string } = {
    [RecommendationFrequency.RARELY]: "😑 Rarely",
    [RecommendationFrequency.SOMETIMES]: "😄 Sometimes",
    [RecommendationFrequency.OFTEN]: "😍 Often",
};

const Row = styled.div<{ removed: boolean }>`
    height: 120px;

    flex: 1;
    background: ${({ theme }) => theme.backgroundSecondary};
    opacity: ${({ removed }) => (removed ? 0.5 : 1)};
    min-width: fit-content;
    overflow: hidden;
    display: grid;
    grid-column: 1 / end;
    grid-template-columns: subgrid;
    grid-auto-flow: column;
    justify-items: stretch;
    align-items: stretch;

    &:not(:first-child) {
        border-top: 1px solid ${({ theme }) => theme.borderPrimary};
    }
`;

const Cell = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;

    min-width: 0;
    min-height: 0;

    &:not(:first-child) {
        border-left: 1px solid ${({ theme }) => theme.borderPrimary};
        padding: 8px;
    }
`;

const Thumbnail = styled.img`
    min-width: 0;
    min-height: 0;
    object-fit: cover;
`;

const IdLabel = styled.span`
    word-break: break-all;
`;

const StyledInput = styled(LifeAtTextInputV2)`
    border: 1px solid ${({ theme }) => theme.borderPrimary};
    width: 100%;
`;

const StyledSelect = styled(LifeAtSingleSelectDropdown)`
    border-radius: 0;
    width: 100%;
`;

const StyledCheckbox = styled(LifeAtCheckbox)`
    margin: 0;
`;

const DeleteButton = styled(LifeAtButtonV2)`
    & .lifeat-button-content {
        padding: 4px;
    }
`;

type AdminSpaceDetailsFormProps = {
    id: string;
    space: Space;
    scrollOnMount: boolean;
    onMetadataChange: (spaceId: string, next: Space) => void;
    onCategoryChange: (spaceId: string, targetCategory: SpaceCategory) => void;
    onDelete: (spaceId: string) => void;
};

export const AdminSpaceDetailsForm = memo((props: AdminSpaceDetailsFormProps) => {
    const rowRef = useRef<HTMLDivElement>(null);

    // Scroll new space into view.
    useEffect(() => {
        if (props.scrollOnMount) {
            rowRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "end",
            });
        }
    }, [props.scrollOnMount]);

    return (
        <Row ref={rowRef} removed={!!props.space.removed}>
            <Cell>
                <Thumbnail src={getSpaceThumbnail(props.space.videoUrl)} />
            </Cell>
            <Cell>
                <IdLabel>{props.id}</IdLabel>
            </Cell>
            <Cell>
                <StyledInput
                    value={props.space.name ?? ""}
                    onChange={(e) =>
                        props.onMetadataChange(props.id, {
                            ...props.space,
                            name: e.target.value,
                        })
                    }
                />
            </Cell>
            <Cell>
                <StyledSelect
                    options={Object.values(SpaceCategory).map((category) => ({
                        label: SpaceCategoryLabels[category],
                        value: category,
                    }))}
                    selected={props.space.category}
                    onChange={(value) => {
                        if (value && value !== props.space.category) {
                            props.onCategoryChange(props.id, value as SpaceCategory);
                        }
                    }}
                />
            </Cell>
            <Cell>
                <StyledInput
                    value={props.space.videoUrl ?? ""}
                    onChange={(e) =>
                        props.onMetadataChange(props.id, {
                            ...props.space,
                            videoUrl: e.target.value,
                        })
                    }
                />
            </Cell>
            <Cell>
                <StyledCheckbox
                    checked={!!props.space.isPremium}
                    handleToggle={() =>
                        props.onMetadataChange(props.id, {
                            ...props.space,
                            isPremium: !props.space.isPremium,
                        })
                    }
                />
            </Cell>
            <Cell>
                <StyledCheckbox
                    checked={!!props.space.featured}
                    handleToggle={() =>
                        props.onMetadataChange(props.id, {
                            ...props.space,
                            featured: !props.space.featured,
                        })
                    }
                />
            </Cell>
            <Cell>
                <StyledCheckbox
                    checked={!!props.space.removed}
                    handleToggle={() =>
                        props.onMetadataChange(props.id, {
                            ...props.space,
                            removed: !props.space.removed,
                        })
                    }
                />
            </Cell>
            <Cell>
                <StyledSelect
                    options={Object.values(RecommendationFrequency).map((quality) => ({
                        label: RecommendationFrequencyLabels[quality],
                        value: String(quality),
                    }))}
                    selected={String(props.space.recommendation)}
                    onChange={(value) => {
                        const next = Number(value) as RecommendationFrequency;
                        if (next && next !== props.space.recommendation) {
                            props.onMetadataChange(props.id, {
                                ...props.space,
                                recommendation: next,
                            });
                        }
                    }}
                />
            </Cell>
            <Cell>
                <StyledInput
                    value={props.space.creatorName ?? ""}
                    onChange={(e) =>
                        props.onMetadataChange(props.id, {
                            ...props.space,
                            creatorName: e.target.value,
                        })
                    }
                />
            </Cell>
            <Cell>
                <StyledInput
                    value={props.space.creatorAviUrl ?? ""}
                    onChange={(e) =>
                        props.onMetadataChange(props.id, {
                            ...props.space,
                            creatorAviUrl: e.target.value,
                        })
                    }
                />
            </Cell>
            <Cell>
                <StyledInput
                    value={props.space.creatorSocialLinks.tiktok ?? ""}
                    onChange={(e) =>
                        props.onMetadataChange(props.id, {
                            ...props.space,
                            creatorSocialLinks: {
                                ...props.space.creatorSocialLinks,
                                tiktok: e.target.value,
                            },
                        })
                    }
                />
            </Cell>
            <Cell>
                <StyledInput
                    value={props.space.creatorSocialLinks.youtube ?? ""}
                    onChange={(e) =>
                        props.onMetadataChange(props.id, {
                            ...props.space,
                            creatorSocialLinks: {
                                ...props.space.creatorSocialLinks,
                                youtube: e.target.value,
                            },
                        })
                    }
                />
            </Cell>
            <Cell>
                <StyledInput
                    value={props.space.creatorSocialLinks.instagram ?? ""}
                    onChange={(e) =>
                        props.onMetadataChange(props.id, {
                            ...props.space,
                            creatorSocialLinks: {
                                ...props.space.creatorSocialLinks,
                                instagram: e.target.value,
                            },
                        })
                    }
                />
            </Cell>
            <Cell>
                <StyledInput
                    value={props.space.creatorSocialLinks.spotify ?? ""}
                    onChange={(e) =>
                        props.onMetadataChange(props.id, {
                            ...props.space,
                            creatorSocialLinks: {
                                ...props.space.creatorSocialLinks,
                                spotify: e.target.value,
                            },
                        })
                    }
                />
            </Cell>
            <Cell>
                <StyledInput
                    value={props.space.creatorSocialLinks.tips ?? ""}
                    onChange={(e) =>
                        props.onMetadataChange(props.id, {
                            ...props.space,
                            creatorSocialLinks: {
                                ...props.space.creatorSocialLinks,
                                tips: e.target.value,
                            },
                        })
                    }
                />
            </Cell>
            <Cell>
                <StyledInput
                    value={props.space.creatorSocialLinks.other ?? ""}
                    onChange={(e) =>
                        props.onMetadataChange(props.id, {
                            ...props.space,
                            creatorSocialLinks: {
                                ...props.space.creatorSocialLinks,
                                other: e.target.value,
                            },
                        })
                    }
                />
            </Cell>
            <Cell>
                <DeleteButton onClick={() => props.onDelete(props.id)}>🗑️</DeleteButton>
            </Cell>
        </Row>
    );
});
